import './styles/main.css';

import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { HelmetProvider } from 'react-helmet-async';
import { BrowserRouter } from 'react-router-dom';

import { RootLayout } from '~/components/RootLayout';
import { isEnvEnabled } from '~/lib/env';
import { AuthenticationProvider } from '~/modules/auth/components/AuthenticationProvider';
import { BrandProvider } from '~/modules/branding/components/BrandProvider';
import { ThemeProvider } from '~/modules/branding/components/ThemeProvider';
import type { BrandType } from '~/modules/branding/types';
import { FeatureFlagProvider } from '~/modules/experiments/components/FeatureFlagProvider';
import { LocaleProvider } from '~/modules/navigation';
import { ContainerQueryClientProvider } from '~/modules/query-client/ContainerQueryClientProvider';

import { AuthRouter } from './authRouter';
import { I18nProvider } from './i18n';

interface AppProps {
  localBrand?: BrandType;
}

export function App({ localBrand }: AppProps) {
  return (
    <I18nProvider>
      <HelmetProvider>
        <BrandProvider
          localBrand={localBrand}
          hostname={window.location.hostname}
        >
          <ThemeProvider>
            <ContainerQueryClientProvider>
              <BrowserRouter>
                <RootLayout>
                  <LocaleProvider>
                    <AuthenticationProvider>
                      <FeatureFlagProvider>
                        <AuthRouter />
                      </FeatureFlagProvider>
                    </AuthenticationProvider>
                  </LocaleProvider>
                </RootLayout>
              </BrowserRouter>
              {isEnvEnabled('VITE_REACT_QUERY_DEVTOOLS_ENABLED', false) ? (
                // https://tanstack.com/query/latest/docs/framework/react/devtools
                <ReactQueryDevtools initialIsOpen={false} />
              ) : null}
            </ContainerQueryClientProvider>
          </ThemeProvider>
        </BrandProvider>
      </HelmetProvider>
    </I18nProvider>
  );
}

App.displayName = 'App';
